import {
  TABLE_ADD_COL_STRIP_WIDTH_PX,
  TABLE_ADD_ROW_STRIP_HEIGHT_PX,
  TABLE_ADD_STRIP_GAP_PX,
} from "./constants";
import { clipLayoutRectToContainer } from "./clip-layout-rect";
import type { TableCtx } from "./types";

/** 与编辑器容器相交后的条带几何；宽表时横向裁剪，避免条带画在容器外。 */
export type AddStripLayoutRect = { top: number; left: number; width: number; height: number };

/** 视口坐标是否落在表格外侧「加行」条未裁剪矩形内（与 `computeAddStripLayoutsFromTableRect` 几何一致）。 */
export function pointHitsTableAddRowStrip(clientX: number, clientY: number, tableRect: DOMRect): boolean {
  const top = tableRect.bottom + TABLE_ADD_STRIP_GAP_PX;
  const left = tableRect.left;
  const w = tableRect.width;
  const h = TABLE_ADD_ROW_STRIP_HEIGHT_PX;
  return clientX >= left && clientX <= left + w && clientY >= top && clientY <= top + h;
}

/** 视口坐标是否落在表格外侧「加列」条未裁剪矩形内。 */
export function pointHitsTableAddColStrip(clientX: number, clientY: number, tableRect: DOMRect): boolean {
  const top = tableRect.top;
  const left = tableRect.right + TABLE_ADD_STRIP_GAP_PX;
  const w = TABLE_ADD_COL_STRIP_WIDTH_PX;
  const h = tableRect.height;
  return clientX >= left && clientX <= left + w && clientY >= top && clientY <= top + h;
}

/**
 * 末尾「加行 / 加列」条：`fixed` 且可能宽于/高于视口，与 `containerRef` 做 **轴对齐裁剪**，
 * 仅渲染落在容器内的矩形（与「是否相交」布尔不同，避免宽表时条带仍横跨到编辑器外）。
 */
export function computeAddStripLayoutsFromTableRect(
  tableRect: DOMRect | null,
  containerRect: DOMRect | null,
): { rowStrip: AddStripLayoutRect | null; colStrip: AddStripLayoutRect | null } {
  if (!tableRect || !containerRect) return { rowStrip: null, colStrip: null };
  const rowStripUnclipped = {
    top: tableRect.bottom + TABLE_ADD_STRIP_GAP_PX,
    left: tableRect.left,
    width: tableRect.width,
    height: TABLE_ADD_ROW_STRIP_HEIGHT_PX,
  };
  const colStripUnclipped = {
    top: tableRect.top,
    left: tableRect.right + TABLE_ADD_STRIP_GAP_PX,
    width: TABLE_ADD_COL_STRIP_WIDTH_PX,
    height: tableRect.height,
  };
  return {
    rowStrip: clipLayoutRectToContainer(rowStripUnclipped, containerRect),
    colStrip: clipLayoutRectToContainer(colStripUnclipped, containerRect),
  };
}

export function computeAddStripLayouts(
  ctx: TableCtx | null,
  containerRect: DOMRect | null,
): { rowStrip: AddStripLayoutRect | null; colStrip: AddStripLayoutRect | null } {
  return computeAddStripLayoutsFromTableRect(ctx?.tableRect ?? null, containerRect);
}
